/**
 * Bits shared by the birthdays card and page. Presentation + tiny pure helpers
 * only — no fetching lives here.
 *
 * Colour here is per-KIND decoration (a cake, a heart, a star), not a member's
 * personal colour, so it never reaches into the family_members palette and never
 * stands alone: every kind carries its own icon AND its label, so the list reads
 * fine in greyscale.
 */
import { useEffect, type ReactNode } from 'react';
import { ApiRequestError } from '../../lib/api';
import type { SpecialDate } from '../../shared/types';

export const cx = (...parts: (string | false | null | undefined)[]) =>
  parts.filter(Boolean).join(' ');

// ---------------------------------------------------------------- controls

export const btnPrimary =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-sage px-5 text-sm font-medium text-white transition-colors hover:bg-sage/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:cursor-not-allowed disabled:opacity-50';

export const btnSecondary =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 text-sm font-medium text-ink transition-colors hover:bg-surface-sunk focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:cursor-not-allowed disabled:opacity-50';

export const btnQuiet =
  'inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl px-3 text-sm font-medium text-ink-soft transition-colors hover:bg-surface-sunk hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:cursor-not-allowed disabled:opacity-50';

export const btnDanger =
  'inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border border-over/40 bg-clay-soft px-4 text-sm font-medium text-clay transition-colors hover:bg-clay-soft/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clay disabled:cursor-not-allowed disabled:opacity-50';

export const fieldLabel = 'block text-sm font-medium text-ink';
export const fieldInput =
  'mt-1 block w-full min-h-[44px] rounded-xl border border-border bg-surface px-3 py-2 text-base text-ink placeholder:text-ink-faint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage';

// ---------------------------------------------------------------- kind meta

interface KindMeta {
  label: string;
  icon: ReactNode;
  /** text colour class for the icon + accent */
  accent: string;
  /** soft background for the icon chip */
  chip: string;
}

const cakeIcon = (
  <>
    <path d="M4 20h16" />
    <path d="M4 20v-6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6" />
    <path d="M4 15c1.2 1 2 1 3.2 0s2-1 3.2 0 2 1 3.2 0 2-1 3.2 0" />
    <path d="M12 8.5v3M8.5 9v2.5M15.5 9v2.5" />
    <path d="M12 4.5c.6.6.6 1.4 0 2-.6-.6-.6-1.4 0-2ZM8.5 5.5c.5.5.5 1.2 0 1.7-.5-.5-.5-1.2 0-1.7ZM15.5 5.5c.5.5.5 1.2 0 1.7-.5-.5-.5-1.2 0-1.7Z" />
  </>
);

const heartIcon = (
  <path d="M12 20s-7-4.4-7-9.5A3.5 3.5 0 0 1 12 8a3.5 3.5 0 0 1 7 2.5C19 15.6 12 20 12 20Z" />
);

const starIcon = (
  <path d="M12 4.5l2.2 4.6 5 .6-3.7 3.4 1 5-4.5-2.5L7.5 18l1-5L4.8 9.7l5-.6L12 4.5Z" />
);

export const KIND_META: Record<SpecialDate['kind'], KindMeta> = {
  birthday: { label: 'Birthday', icon: cakeIcon, accent: 'text-honey', chip: 'bg-honey-soft' },
  anniversary: { label: 'Anniversary', icon: heartIcon, accent: 'text-clay', chip: 'bg-clay-soft' },
  other: { label: 'Special day', icon: starIcon, accent: 'text-dusk', chip: 'bg-dusk-soft' },
};

/** The kind icon in its own soft chip. Decorative — the label always sits beside it. */
export function KindIcon({ kind, size = 'md' }: { kind: SpecialDate['kind']; size?: 'sm' | 'md' }) {
  const meta = KIND_META[kind];
  const box = size === 'sm' ? 'h-9 w-9' : 'h-11 w-11';
  const svg = size === 'sm' ? 'h-5 w-5' : 'h-6 w-6';
  return (
    <span className={cx('inline-grid shrink-0 place-items-center rounded-full', box, meta.chip)}>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cx(svg, meta.accent)}
        aria-hidden="true"
        focusable="false"
      >
        {meta.icon}
      </svg>
    </span>
  );
}

// ---------------------------------------------------------------- dates

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const monthName = (m: number) => MONTHS_LONG[m - 1] ?? '';

/** Days in a month, leap-year aware. 2000 (leap) lets Feb offer 29 when year unknown. */
export function daysInMonth(year: number, month: number): number {
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  return [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 31;
}

function parseCivil(iso: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

/**
 * Format a YYYY-MM-DD civil date as e.g. "Sat, Sep 5" (adds the year if it isn't the
 * current one). Built at LOCAL noon so it never shifts a day across timezones the way
 * `new Date('2026-09-05')` (parsed as UTC) can.
 */
export function formatDate(iso: string): string {
  const c = parseCivil(iso);
  if (!c) return '';
  const dt = new Date(c.y, c.m - 1, c.d, 12);
  const sameYear = c.y === new Date().getFullYear();
  return dt.toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** A soft, honest countdown. The exact date is always shown alongside it. */
export function daysAwayLabel(days: number): string {
  if (days <= 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days <= 21) return `In ${days} days`;
  if (days <= 70) return `In ${Math.round(days / 7)} weeks`;
  return `In ${Math.round(days / 30)} months`;
}

/** "Turning 12" / "12 years" — only when the year, and so the count, is known. */
export function turningLabel(date: SpecialDate): string | null {
  if (date.turning == null) return null;
  if (date.kind === 'birthday') return `Turning ${date.turning}`;
  return `${date.turning} year${date.turning === 1 ? '' : 's'}`;
}

// ---------------------------------------------------------------- errors

/**
 * Say what actually happened. Never state a cause we haven't established — an
 * unreachable API is not "not allowed", and a 500 is not "check your wifi".
 */
export function explainError(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiRequestError) {
    if (err.body.error === 'api_unreachable')
      return 'The app loaded, but the API is not answering on this address.';
    if (err.body.detail) return err.body.detail;
    if (err.status === 403) return 'Only a grown-up can change these dates.';
    if (err.status === 404) return 'That date is gone.';
    if (err.status >= 500) return 'The app is up but the server had a problem. Try again.';
    return fallback;
  }
  if (err instanceof TypeError) return 'Could not reach the app. Check your connection.';
  return fallback;
}

// ---------------------------------------------------------------- shells

export function Banner({ tone, children }: { tone: 'ok' | 'error'; children: ReactNode }) {
  return (
    <p
      role="status"
      className={cx(
        'flex items-start gap-2 rounded-xl border px-3 py-2 text-sm',
        tone === 'ok'
          ? 'border-sage/30 bg-sage-soft text-sage'
          : 'border-over/40 bg-clay-soft text-clay'
      )}
    >
      <span aria-hidden="true" className="font-semibold">
        {tone === 'ok' ? '✓' : '!'}
      </span>
      <span>{children}</span>
    </p>
  );
}

export function LoadingRows({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3" role="status" aria-live="polite">
      <span className="sr-only">Loading dates…</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-11 w-11 shrink-0 animate-pulse rounded-full bg-surface-sunk" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-32 animate-pulse rounded-full bg-surface-sunk" />
            <div
              className={cx(
                'h-3 animate-pulse rounded-full bg-surface-sunk',
                i % 2 === 0 ? 'w-40' : 'w-24'
              )}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="space-y-3 rounded-xl border border-over/40 bg-clay-soft p-4">
      <p className="text-sm font-medium text-ink">These dates didn&rsquo;t load.</p>
      <p className="text-sm text-ink-soft">{message}</p>
      <button type="button" onClick={onRetry} className={btnSecondary}>
        Try again
      </button>
    </div>
  );
}

/** A small centred dialog. Escape closes it; the backdrop does too. */
export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const titleId = `dlg-${title.replace(/\W+/g, '-').toLowerCase()}`;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-card border border-border bg-surface p-5 shadow-xl sm:rounded-card"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={titleId} className="font-display text-lg font-semibold text-ink">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="-mt-1 -mr-1 grid h-11 w-11 place-items-center rounded-xl text-ink-soft hover:bg-surface-sunk hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            <span aria-hidden="true" className="text-lg leading-none">
              ✕
            </span>
            <span className="sr-only">Close</span>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
